import { Injectable } from "@angular/core";
import { HttpClient, HttpEvent, HttpHeaders } from "@angular/common/http";
import { ApiService } from "./api.service";
import { Router } from "@angular/router";
import { v4 as uuid } from "uuid";
import { tap } from "rxjs/operators";
import { Observable } from "rxjs";
import { environment } from "../../environments/environment";
import { LoggedInService } from "./logged-in.service";

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  private readonly ACCESS_TOKEN = "ACCESS_TOKEN";
  private readonly REFRESH_TOKEN = "REFRESH_TOKEN";
  private readonly STATE = "STATE";
  private readonly CODE_VERIFIER = "CODE_VERIFIER";

  constructor(
    private http: HttpClient,
    private api: ApiService,
    private router: Router,
    private loggedIn: LoggedInService,
  ) {}

  private getAuthHeaders(): Object {
    let headers = new HttpHeaders().append(
      "Content-Type",
      "application/x-www-form-urlencoded",
    );
    return { headers: headers };
  }

  public storeAccessToken(token: string) {
    localStorage.setItem(this.ACCESS_TOKEN, token);
  }

  public getAccessToken(): string {
    return localStorage.getItem(this.ACCESS_TOKEN);
  }

  public storeRefreshToken(token: string) {
    localStorage.setItem(this.REFRESH_TOKEN, token);
  }

  public getRefreshToken(): string {
    return localStorage.getItem(this.REFRESH_TOKEN);
  }

  public storeState(state: string) {
    localStorage.setItem(this.STATE, state);
  }

  public getState(): string {
    return localStorage.getItem(this.STATE);
  }

  public async requestAuthCode(redirect?: string) {
    let state = uuid();
    let stateObj = {
      id: state,
      application: this.api.APPLICATION,
      platform: this.api.PLATFORM,
      redirect: redirect,
    };
    let encoded = btoa(JSON.stringify(stateObj));
    this.storeState(encoded);

    // Generate and store a PKCE code_verifier, then compute the code_challenge
    const code_verifier = this.generateCodeVerifier();
    // generateCodeVerifier already stores the verifier in localStorage
    const code_challenge = await this.generateCodeChallenge(code_verifier);
    const code_challenge_method = "S256";

    window.location.href = `${environment.ssoUrl}/authorize?
      state=${encodeURIComponent(encoded)}&
      scope=openid&
      client_id=${encodeURIComponent(environment.clientId)}&
      redirect_uri=${encodeURIComponent(environment.redirectUrl)}&
      response_type=code&
      token_content_type=jwt&
      code_challenge=${encodeURIComponent(code_challenge)}&
      code_challenge_method=${encodeURIComponent(code_challenge_method)}
    `.replace(/\s+/g, "");
  }

  public requestAccessToken(code: string, redirect?: string): Promise<any> {
    const code_verifier = localStorage.getItem(this.CODE_VERIFIER);
    if (!code_verifier) {
      return Promise.reject(
        new Error(
          "PKCE code_verifier missing from storage. Start auth flow again.",
        ),
      );
    }

    const params = `
      code=${encodeURIComponent(code)}&
      redirect_uri=${encodeURIComponent(environment.redirectUrl)}&
      client_id=${encodeURIComponent(environment.clientId)}&
      grant_type=authorization_code&
      token_content_type=jwt&
      code_verifier=${encodeURIComponent(code_verifier)}
    `.replace(/\s+/g, "");
    return this.http
      .post<any>(
        `${environment.ssoUrl}/token`,
        new Blob([params], { type: "application/json" }),
        this.getAuthHeaders(),
      )
      .toPromise()
      .then((response) => {
        if (response && response.id_token) {
          this.storeAccessToken(response.id_token);
        }
        if (response && response.refresh_token) {
          this.storeRefreshToken(response.refresh_token);
        }
        if (redirect && redirect.indexOf(window.location.origin) !== -1) {
          this.router.navigateByUrl(redirect.split(window.location.origin)[1]);
        }
        return response;
      });
  }

  public refreshAccessToken(): Observable<HttpEvent<unknown>> {
    let params = `
      refresh_token=${this.getRefreshToken()}&
      redirect_uri=${environment.redirectUrl}&
      client_id=${environment.clientId}&
      grant_type=refresh_token&
      token_content_type=jwt
    `.replace(/\s+/g, "");
    return this.http
      .post<any>(
        `${environment.ssoUrl}/token`,
        new Blob([params], { type: "application/json" }),
        this.getAuthHeaders(),
      )
      .pipe(
        tap((response: any) => {
          if (response && response.access_token) {
            this.storeAccessToken(response.access_token);
          }
          if (response && response.refresh_token) {
            this.storeRefreshToken(response.refresh_token);
          }
        }),
      );
  }

  // Helper: base64url encode an ArrayBuffer
  private base64UrlEncode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const b64 = btoa(binary);
    // base64url: replace +/ with -_, remove padding
    return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }

  // Compute the code_challenge from a code_verifier using SHA-256 and base64url encoding
  private async generateCodeChallenge(code_verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(code_verifier);
    const digest = await window.crypto.subtle.digest("SHA-256", data);
    return this.base64UrlEncode(digest);
  }

  // Generate a cryptographically-random code_verifier (PKCE) and store it
  private generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    // Convert to base64url
    let binary = "";
    for (let i = 0; i < array.byteLength; i++) {
      binary += String.fromCharCode(array[i]);
    }
    const b64 = btoa(binary);
    const code_verifier = b64
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
    localStorage.setItem(this.CODE_VERIFIER, code_verifier);
    return code_verifier;
  }

  public logout() {
    this.storeAccessToken("");
    this.storeRefreshToken("");
    this.router.navigateByUrl("/start");
    this.loggedIn.toggle(false);
  }
}
